import { useState, useEffect } from "react";

// ─── SUPABASE CONFIG ───────────────────────────────────────────────────────────
// Replace with your actual Supabase URL and anon key
// Create a table called "referrals" with columns matching the form fields + score + level
const SUPABASE_URL = "https://aqkhetmicodgmpxavetx.supabase.co";
const SUPABASE_KEY = "sb_publishable_cWfHombgMllpakQnC2duSw_Wjnil7-7";

async function saveReferral(data) {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/referrals`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${SUPABASE_KEY}`,
      Prefer: "return=representation",
    },
    body: JSON.stringify(data),
  });
  return res.json();
}

async function fetchReferrals() {
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/referrals?order=created_at.desc`,
    {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${SUPABASE_KEY}`,
      },
    }
  );
  return res.json();
}

// ─── SCORING ENGINE ────────────────────────────────────────────────────────────
function calculatePlacementScore(form) {
  let score = 0;

  // Mobility (0-3)
  const mobilityMap = { independent: 0, "minimal-assist": 1, "moderate-assist": 2, "full-assist": 3 };
  score += mobilityMap[form.mobility] ?? 0;

  // ADL needs (0-3)
  const adlMap = { independent: 0, "some-help": 1, "significant-help": 2, "total-care": 3 };
  score += adlMap[form.adl] ?? 0;

  // Fall risk (0-2)
  const fallMap = { low: 0, moderate: 1, high: 2 };
  score += fallMap[form.fallRisk] ?? 0;

  // Cognitive status (0-3)
  const cogMap = { intact: 0, "mild-impairment": 1, "moderate-impairment": 2, "severe-impairment": 3 };
  score += cogMap[form.cognitive] ?? 0;

  // Medication support (0-2)
  const medMap = { self: 0, "needs-reminders": 1, "full-management": 2 };
  score += medMap[form.medication] ?? 0;

  // Safe alone
  if (form.safeAlone === "no") score += 3;
  else if (form.safeAlone === "with-check-ins") score += 1;

  return score;
}

function getPlacementLevel(score) {
  if (score <= 4) return { level: 1, label: "Level 1: Independent Support", color: "#0D9488", bg: "#F0FDFA", border: "#99F6E4" };
  if (score <= 9) return { level: 2, label: "Level 2: Supervised Recovery", color: "#0284C7", bg: "#F0F9FF", border: "#BAE6FD" };
  return { level: 3, label: "Level 3: High Acuity Support", color: "#1E3A5F", bg: "#EFF6FF", border: "#BFDBFE" };
}

function getLevelDescription(level) {
  const desc = {
    1: "Patient demonstrates sufficient independence for community-based support. Recommend home health services, outpatient follow-up, and periodic wellness check-ins.",
    2: "Patient requires structured oversight and daily support. Recommend transitional care placement at a VitalBridge Recovery Village or supervised nursing facility.",
    3: "Patient requires intensive, around-the-clock care and monitoring. Recommend skilled nursing facility or VitalBridge High Acuity Recovery Village placement.",
  };
  return desc[level];
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const styles = {
  root: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    background: "#F4F6F9",
    minHeight: "100vh",
    color: "#1E2D3D",
  },
  header: {
    background: "linear-gradient(135deg, #1E3A5F 0%, #0D4F7A 60%, #0D9488 100%)",
    padding: "0",
    boxShadow: "0 4px 20px rgba(30,58,95,0.25)",
  },
  headerInner: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "20px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
  },
  logo: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  logoMark: {
    width: 46,
    height: 46,
    background: "rgba(255,255,255,0.15)",
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(255,255,255,0.2)",
  },
  logoText: {
    color: "white",
  },
  logoTitle: {
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: "-0.3px",
    margin: 0,
    lineHeight: 1.2,
  },
  logoSub: {
    fontSize: 11,
    opacity: 0.75,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    margin: 0,
  },
  navTabs: {
    display: "flex",
    gap: 4,
  },
  navTab: (active) => ({
    padding: "8px 20px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    transition: "all 0.2s",
    background: active ? "rgba(255,255,255,0.2)" : "transparent",
    color: active ? "white" : "rgba(255,255,255,0.65)",
    backdropFilter: active ? "blur(8px)" : "none",
  }),
  main: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "32px 24px 60px",
  },
  card: {
    background: "white",
    borderRadius: 16,
    boxShadow: "0 2px 16px rgba(30,58,95,0.08)",
    border: "1px solid #E8EDF2",
    overflow: "hidden",
  },
  cardHeader: {
    padding: "24px 32px",
    borderBottom: "1px solid #F0F4F8",
    background: "linear-gradient(to right, #FAFCFF, #F7FDFC)",
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: "#1E3A5F",
    margin: "0 0 4px",
  },
  cardSub: {
    fontSize: 14,
    color: "#6B7A8D",
    margin: 0,
  },
  cardBody: {
    padding: "32px",
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    color: "#0D9488",
    margin: "0 0 16px",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  sectionDivider: {
    height: 1,
    background: "#F0F4F8",
    margin: "28px 0",
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 20,
  },
  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 20,
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
  },
  required: {
    color: "#EF4444",
    marginLeft: 3,
  },
  input: {
    padding: "10px 14px",
    borderRadius: 8,
    border: "1.5px solid #E5E7EB",
    fontSize: 14,
    color: "#1E2D3D",
    background: "#FAFAFA",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    width: "100%",
    boxSizing: "border-box",
  },
  select: {
    padding: "10px 14px",
    borderRadius: 8,
    border: "1.5px solid #E5E7EB",
    fontSize: 14,
    color: "#1E2D3D",
    background: "#FAFAFA",
    outline: "none",
    transition: "border-color 0.2s",
    width: "100%",
    boxSizing: "border-box",
    cursor: "pointer",
  },
  textarea: {
    padding: "10px 14px",
    borderRadius: 8,
    border: "1.5px solid #E5E7EB",
    fontSize: 14,
    color: "#1E2D3D",
    background: "#FAFAFA",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    resize: "vertical",
    minHeight: 90,
    fontFamily: "inherit",
  },
  submitBtn: {
    background: "linear-gradient(135deg, #1E3A5F, #0D9488)",
    color: "white",
    border: "none",
    padding: "14px 36px",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(13,148,136,0.35)",
    transition: "all 0.2s",
    letterSpacing: "0.2px",
  },
  resultCard: (bg, border) => ({
    background: bg,
    border: `2px solid ${border}`,
    borderRadius: 16,
    padding: "28px 32px",
    marginTop: 28,
  }),
  levelBadge: (color) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: color,
    color: "white",
    padding: "8px 18px",
    borderRadius: 100,
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 14,
    boxShadow: `0 4px 12px ${color}55`,
  }),
  scoreBar: {
    height: 8,
    borderRadius: 100,
    background: "#E5E7EB",
    marginTop: 10,
    overflow: "hidden",
  },
  scoreBarFill: (score, color) => ({
    height: "100%",
    width: `${Math.min((score / 16) * 100, 100)}%`,
    background: color,
    borderRadius: 100,
    transition: "width 0.8s ease",
  }),
  aiPlaceholder: {
    background: "linear-gradient(135deg, #F8FAFF, #F0FDFA)",
    border: "1.5px dashed #BAE6FD",
    borderRadius: 12,
    padding: "18px 22px",
    marginTop: 18,
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
  },
  transportBadge: (needed) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    borderRadius: 100,
    fontSize: 13,
    fontWeight: 600,
    background: needed ? "#FFF7ED" : "#F0FDFA",
    color: needed ? "#C2410C" : "#0D9488",
    border: `1px solid ${needed ? "#FED7AA" : "#99F6E4"}`,
  }),
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
  },
  th: {
    textAlign: "left",
    padding: "12px 16px",
    background: "#F8FAFC",
    color: "#6B7A8D",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "1px",
    textTransform: "uppercase",
    borderBottom: "2px solid #E8EDF2",
  },
  td: {
    padding: "14px 16px",
    borderBottom: "1px solid #F0F4F8",
    color: "#1E2D3D",
    verticalAlign: "middle",
  },
  levelPill: (level) => {
    const colors = {
      1: { bg: "#F0FDFA", color: "#0D9488", border: "#99F6E4" },
      2: { bg: "#F0F9FF", color: "#0284C7", border: "#BAE6FD" },
      3: { bg: "#EFF6FF", color: "#1E3A5F", border: "#BFDBFE" },
    };
    const c = colors[level] || colors[1];
    return {
      display: "inline-block",
      padding: "4px 10px",
      borderRadius: 100,
      fontSize: 12,
      fontWeight: 700,
      background: c.bg,
      color: c.color,
      border: `1px solid ${c.border}`,
    };
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#9CA3AF",
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 16,
    marginBottom: 28,
  },
  statCard: (color) => ({
    background: "white",
    borderRadius: 12,
    padding: "20px 22px",
    border: `1px solid #E8EDF2`,
    borderLeft: `4px solid ${color}`,
    boxShadow: "0 2px 8px rgba(30,58,95,0.06)",
  }),
  statNum: (color) => ({
    fontSize: 32,
    fontWeight: 800,
    color,
    lineHeight: 1,
    margin: "0 0 4px",
  }),
  statLabel: {
    fontSize: 12,
    color: "#6B7A8D",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  successOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(30,58,95,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    backdropFilter: "blur(4px)",
  },
  successBox: {
    background: "white",
    borderRadius: 20,
    padding: "48px 40px",
    textAlign: "center",
    maxWidth: 420,
    width: "90%",
    boxShadow: "0 20px 60px rgba(30,58,95,0.25)",
  },
};

// ─── FORM INITIAL STATE ────────────────────────────────────────────────────────
const initialForm = {
  patientName: "",
  referralSource: "",
  dischargeDate: "",
  mobility: "",
  adl: "",
  fallRisk: "",
  cognitive: "",
  medication: "",
  safeAlone: "",
  transportation: "",
  insurance: "",
  veteran: "",
  notes: "",
};

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function VitalBridgeApp() {
  const [tab, setTab] = useState("referral");
  const [form, setForm] = useState(initialForm);
  const [result, setResult] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [useSupabase] = useState(true);

  // Load referrals from localStorage if Supabase not configured
  useEffect(() => {
    if (tab === "dashboard") {
      if (useSupabase) {
        fetchReferrals().then((data) => Array.isArray(data) && setReferrals(data));
      } else {
        const stored = JSON.parse(localStorage.getItem("vb_referrals") || "[]");
        setReferrals(stored);
      }
    }
  }, [tab, useSupabase]);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!form.patientName || !form.referralSource || !form.dischargeDate) {
      alert("Please fill in Patient Name, Referral Source, and Discharge Date.");
      return;
    }
    if (!form.mobility || !form.adl || !form.fallRisk || !form.cognitive || !form.medication || !form.safeAlone) {
      alert("Please complete all clinical assessment fields.");
      return;
    }

    setSubmitting(true);
    const score = calculatePlacementScore(form);
    const placement = getPlacementLevel(score);
    const payload = { ...form, score, placement_level: placement.level, placement_label: placement.label, created_at: new Date().toISOString() };

    if (useSupabase) {
      await saveReferral(payload);
    } else {
      const stored = JSON.parse(localStorage.getItem("vb_referrals") || "[]");
      stored.unshift({ id: Date.now(), ...payload });
      localStorage.setItem("vb_referrals", JSON.stringify(stored));
    }

    setResult({ score, placement, form: { ...form } });
    setSubmitting(false);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2200);
  };

  const resetForm = () => {
    setForm(initialForm);
    setResult(null);
  };

  // Stats for dashboard
  const stats = {
    total: referrals.length,
    l1: referrals.filter((r) => r.placement_level === 1).length,
    l2: referrals.filter((r) => r.placement_level === 2).length,
    l3: referrals.filter((r) => r.placement_level === 3).length,
    transport: referrals.filter((r) => r.transportation === "yes").length,
    veteran: referrals.filter((r) => r.veteran === "yes").length,
  };

  return (
    <div style={styles.root}>
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        input:focus, select:focus, textarea:focus {
          border-color: #0D9488 !important;
          box-shadow: 0 0 0 3px rgba(13,148,136,0.12) !important;
        }
        button:hover { opacity: 0.92; transform: translateY(-1px); }
        tr:hover td { background: #FAFCFF; }
        @media (max-width: 600px) {
          .card-body { padding: 20px !important; }
          .header-inner { flex-direction: column; align-items: flex-start; }
        }
      `}</style>

      {/* HEADER */}
      <header style={styles.header}>
        <div style={styles.headerInner} className="header-inner">
          <div style={styles.logo}>
            <div style={styles.logoMark}>🏥</div>
            <div style={styles.logoText}>
              <p style={styles.logoTitle}>VitalBridge Health</p>
              <p style={styles.logoSub}>Connecting Care Beyond Discharge</p>
            </div>
          </div>
          <nav style={styles.navTabs}>
            <button style={styles.navTab(tab === "referral")} onClick={() => setTab("referral")}>
              📋 New Referral
            </button>
            <button style={styles.navTab(tab === "dashboard")} onClick={() => setTab("dashboard")}>
              📊 Dashboard
            </button>
          </nav>
        </div>
      </header>

      <main style={styles.main}>
        {/* SUCCESS OVERLAY */}
        {showSuccess && (
          <div style={styles.successOverlay}>
            <div style={styles.successBox}>
              <div style={{ fontSize: 52, marginBottom: 16 }}>✅</div>
              <h2 style={{ color: "#1E3A5F", margin: "0 0 8px", fontSize: 22 }}>Referral Submitted</h2>
              <p style={{ color: "#6B7A8D", margin: 0 }}>Placement assessment complete. Scroll down to view results.</p>
            </div>
          </div>
        )}

        {/* ── REFERRAL FORM TAB ── */}
        {tab === "referral" && (
          <>
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>Patient Placement Assessment</h2>
                <p style={styles.cardSub}>Complete all sections to generate a placement recommendation score</p>
              </div>
              <div style={{ ...styles.cardBody }} className="card-body">

                {/* SECTION 1 */}
                <p style={styles.sectionLabel}>
                  <span style={{ width: 20, height: 20, background: "#0D9488", borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 800 }}>1</span>
                  Referral Information
                </p>
                <div style={styles.grid2}>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Patient Name <span style={styles.required}>*</span></label>
                    <input style={styles.input} name="patientName" value={form.patientName} onChange={handleChange} placeholder="Full legal name" />
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Referral Source <span style={styles.required}>*</span></label>
                    <input style={styles.input} name="referralSource" value={form.referralSource} onChange={handleChange} placeholder="Hospital / Facility name" />
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Discharge Date <span style={styles.required}>*</span></label>
                    <input style={styles.input} type="date" name="dischargeDate" value={form.dischargeDate} onChange={handleChange} />
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Insurance Status</label>
                    <select style={styles.select} name="insurance" value={form.insurance} onChange={handleChange}>
                      <option value="">Select...</option>
                      <option value="medicare">Medicare</option>
                      <option value="medicaid">Medicaid</option>
                      <option value="private">Private Insurance</option>
                      <option value="uninsured">Uninsured / Self-Pay</option>
                      <option value="va">VA Benefits</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div style={styles.sectionDivider} />

                {/* SECTION 2 */}
                <p style={styles.sectionLabel}>
                  <span style={{ width: 20, height: 20, background: "#0D9488", borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 800 }}>2</span>
                  Clinical Assessment
                </p>
                <div style={styles.grid3}>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Mobility Status <span style={styles.required}>*</span></label>
                    <select style={styles.select} name="mobility" value={form.mobility} onChange={handleChange}>
                      <option value="">Select...</option>
                      <option value="independent">Independent</option>
                      <option value="minimal-assist">Minimal Assist</option>
                      <option value="moderate-assist">Moderate Assist</option>
                      <option value="full-assist">Full Assist</option>
                    </select>
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>ADL Needs <span style={styles.required}>*</span></label>
                    <select style={styles.select} name="adl" value={form.adl} onChange={handleChange}>
                      <option value="">Select...</option>
                      <option value="independent">Independent</option>
                      <option value="some-help">Some Help</option>
                      <option value="significant-help">Significant Help</option>
                      <option value="total-care">Total Care</option>
                    </select>
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Fall Risk <span style={styles.required}>*</span></label>
                    <select style={styles.select} name="fallRisk" value={form.fallRisk} onChange={handleChange}>
                      <option value="">Select...</option>
                      <option value="low">Low</option>
                      <option value="moderate">Moderate</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Cognitive Status <span style={styles.required}>*</span></label>
                    <select style={styles.select} name="cognitive" value={form.cognitive} onChange={handleChange}>
                      <option value="">Select...</option>
                      <option value="intact">Intact</option>
                      <option value="mild-impairment">Mild Impairment</option>
                      <option value="moderate-impairment">Moderate Impairment</option>
                      <option value="severe-impairment">Severe Impairment</option>
                    </select>
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Medication Support <span style={styles.required}>*</span></label>
                    <select style={styles.select} name="medication" value={form.medication} onChange={handleChange}>
                      <option value="">Select...</option>
                      <option value="self">Self-Manages</option>
                      <option value="needs-reminders">Needs Reminders</option>
                      <option value="full-management">Full Management</option>
                    </select>
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Safe Alone? <span style={styles.required}>*</span></label>
                    <select style={styles.select} name="safeAlone" value={form.safeAlone} onChange={handleChange}>
                      <option value="">Select...</option>
                      <option value="yes">Yes</option>
                      <option value="with-check-ins">Yes, with check-ins</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>

                <div style={styles.sectionDivider} />

                {/* SECTION 3 */}
                <p style={styles.sectionLabel}>
                  <span style={{ width: 20, height: 20, background: "#0D9488", borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 800 }}>3</span>
                  Support & Logistics
                </p>
                <div style={styles.grid3}>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Transportation Needed</label>
                    <select style={styles.select} name="transportation" value={form.transportation} onChange={handleChange}>
                      <option value="">Select...</option>
                      <option value="yes">Yes – needs transport</option>
                      <option value="no">No – has own transport</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Veteran Status</label>
                    <select style={styles.select} name="veteran" value={form.veteran} onChange={handleChange}>
                      <option value="">Select...</option>
                      <option value="yes">Yes – Veteran</option>
                      <option value="no">No</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginTop: 20 }}>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Clinical Notes</label>
                    <textarea style={styles.textarea} name="notes" value={form.notes} onChange={handleChange} placeholder="Additional context, diagnoses, family situation, social history..." />
                  </div>
                </div>

                <div style={{ marginTop: 28, display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <button style={styles.submitBtn} onClick={handleSubmit} disabled={submitting}>
                    {submitting ? "⏳ Calculating..." : "🔍 Submit & Get Placement Score"}
                  </button>
                  <button
                    onClick={resetForm}
                    style={{ ...styles.submitBtn, background: "white", color: "#6B7A8D", boxShadow: "none", border: "1.5px solid #E5E7EB" }}
                  >
                    Reset Form
                  </button>
                </div>
              </div>
            </div>

            {/* RESULTS */}
            {result && (
              <div style={styles.resultCard(result.placement.bg, result.placement.border)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
                  <div>
                    <div style={styles.levelBadge(result.placement.color)}>
                      {result.placement.level === 1 ? "🟢" : result.placement.level === 2 ? "🔵" : "🔷"} {result.placement.label}
                    </div>
                    <p style={{ margin: "0 0 16px", color: "#374151", fontSize: 15, maxWidth: 560, lineHeight: 1.6 }}>
                      {getLevelDescription(result.placement.level)}
                    </p>
                  </div>
                  <div style={{ textAlign: "right", minWidth: 120 }}>
                    <div style={{ fontSize: 42, fontWeight: 800, color: result.placement.color, lineHeight: 1 }}>{result.score}</div>
                    <div style={{ fontSize: 12, color: "#6B7A8D", fontWeight: 600, letterSpacing: "0.5px" }}>ACUITY SCORE</div>
                    <div style={styles.scoreBar}>
                      <div style={styles.scoreBarFill(result.score, result.placement.color)} />
                    </div>
                    <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>Max: 16</div>
                  </div>
                </div>

                {/* Transport Summary */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Transportation:</span>
                  <span style={styles.transportBadge(result.form.transportation === "yes")}>
                    {result.form.transportation === "yes" ? "🚐 EMK Transport Required" : result.form.transportation === "no" ? "✅ Transport Arranged" : "❓ Unknown"}
                  </span>
                  {result.form.veteran === "yes" && (
                    <span style={{ ...styles.transportBadge(false), background: "#F5F3FF", color: "#7C3AED", border: "1px solid #DDD6FE" }}>
                      🎖️ Veteran — VA Benefits Eligible
                    </span>
                  )}
                </div>

                {/* AI Placeholder */}
                <div style={styles.aiPlaceholder}>
                  <span style={{ fontSize: 22 }}>🤖</span>
                  <div>
                    <p style={{ margin: "0 0 4px", fontWeight: 700, color: "#1E3A5F", fontSize: 14 }}>AI Explanation — Coming Soon</p>
                    <p style={{ margin: 0, color: "#6B7A8D", fontSize: 13, lineHeight: 1.5 }}>
                      Future AI-powered narrative: VitalBridge AI will explain exactly why this placement level was recommended, flag clinical risk factors, suggest care interventions, and predict 30-day readmission probability based on patient profile.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── DASHBOARD TAB ── */}
        {tab === "dashboard" && (
          <>
            {/* Stats */}
            <div style={styles.statsRow}>
              <div style={styles.statCard("#1E3A5F")}>
                <p style={styles.statNum("#1E3A5F")}>{stats.total}</p>
                <p style={styles.statLabel}>Total Referrals</p>
              </div>
              <div style={styles.statCard("#0D9488")}>
                <p style={styles.statNum("#0D9488")}>{stats.l1}</p>
                <p style={styles.statLabel}>Level 1 – Independent</p>
              </div>
              <div style={styles.statCard("#0284C7")}>
                <p style={styles.statNum("#0284C7")}>{stats.l2}</p>
                <p style={styles.statLabel}>Level 2 – Supervised</p>
              </div>
              <div style={styles.statCard("#4F46E5")}>
                <p style={styles.statNum("#4F46E5")}>{stats.l3}</p>
                <p style={styles.statLabel}>Level 3 – High Acuity</p>
              </div>
              <div style={styles.statCard("#F59E0B")}>
                <p style={styles.statNum("#F59E0B")}>{stats.transport}</p>
                <p style={styles.statLabel}>Transport Needed</p>
              </div>
              <div style={styles.statCard("#7C3AED")}>
                <p style={styles.statNum("#7C3AED")}>{stats.veteran}</p>
                <p style={styles.statLabel}>Veterans</p>
              </div>
            </div>

            {/* Table */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>Referral Dashboard</h2>
                <p style={styles.cardSub}>
                  All submitted patient referrals — {useSupabase ? "connected to Supabase" : "stored locally (connect Supabase for production)"}
                </p>
              </div>
              <div style={{ overflowX: "auto" }}>
                {referrals.length === 0 ? (
                  <div style={styles.emptyState}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
                    <p style={{ fontSize: 16, fontWeight: 600 }}>No referrals yet</p>
                    <p style={{ fontSize: 14 }}>Submit a referral from the New Referral tab to see it here.</p>
                  </div>
                ) : (
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Patient</th>
                        <th style={styles.th}>Referral Source</th>
                        <th style={styles.th}>Discharge</th>
                        <th style={styles.th}>Score</th>
                        <th style={styles.th}>Placement</th>
                        <th style={styles.th}>Transport</th>
                        <th style={styles.th}>Veteran</th>
                        <th style={styles.th}>Insurance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.map((r, i) => (
                        <tr key={r.id || i}>
                          <td style={{ ...styles.td, fontWeight: 600 }}>{r.patientName}</td>
                          <td style={styles.td}>{r.referralSource}</td>
                          <td style={styles.td}>{r.dischargeDate}</td>
                          <td style={{ ...styles.td, fontWeight: 700, color: "#1E3A5F" }}>{r.score}</td>
                          <td style={styles.td}>
                            <span style={styles.levelPill(r.placement_level)}>
                              L{r.placement_level}
                            </span>
                          </td>
                          <td style={styles.td}>
                            {r.transportation === "yes" ? (
                              <span style={{ color: "#C2410C", fontWeight: 600, fontSize: 13 }}>🚐 Needed</span>
                            ) : (
                              <span style={{ color: "#6B7A8D", fontSize: 13 }}>—</span>
                            )}
                          </td>
                          <td style={styles.td}>
                            {r.veteran === "yes" ? (
                              <span style={{ color: "#7C3AED", fontWeight: 600, fontSize: 13 }}>🎖️ Yes</span>
                            ) : (
                              <span style={{ color: "#6B7A8D", fontSize: 13 }}>—</span>
                            )}
                          </td>
                          <td style={{ ...styles.td, textTransform: "capitalize", fontSize: 13 }}>{r.insurance || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
